import React from 'react'

import { Color, Link, LinkProps, LinkVariant, withCitrusTheme } from '@gousto-internal/citrus-react'

export type CWCLinkButtonVariant = 'primary' | 'secondary'

export type CWCLinkButtonColorMode = 'light' | 'dark'

export type CWCLinkButtonProps = {
  href: string
  children: React.ReactNode
  /**
   * Receives the click event so signup-bound CTAs can `preventDefault()` the raw anchor
   * navigation and dispatch the promo/redirect flow instead. CTAs to other pages can ignore
   * the event and just navigate via `href` while tracking the click.
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  /**
   * `primary` (default) = solid purple fill. `secondary` = transparent fill with a 2px purple
   * outline and purple text.
   */
  variant?: CWCLinkButtonVariant
  /**
   * Passed through to the underlying anchor — lets a caller pull this CTA out of (or back into)
   * the tab order (e.g. CWCFloatingCTA while it's visually hidden) without a new component.
   */
  tabIndex?: number
  /**
   * `light` (default) = the shared `Secondary_400`/`Secondary_50`/`Secondary_600` tokens, for a
   * button on a light background. `dark` = for a button on a dark background — Citrus doesn't
   * have dark-mode button tokens yet (confirmed with design), so these are this component's own
   * values until Citrus ships proper ones (all AA on the dark section, see `DARK_MODE_COLOR` below):
   * rest `Secondary_200` (`#A29EFF`), hover `Secondary_100` (`#CBC7FF`), White focus outline, plus a
   * white 16%-opacity overlay wash on hover (not a solid fill change).
   */
  colorMode?: CWCLinkButtonColorMode
  /**
   * Font-size override. Pass a single string (e.g. `'18px'`) to apply one size at every breakpoint,
   * or a `theme.responsiveStyle` array (e.g. `['15px','15px','15px','16px']`) to vary it per breakpoint.
   */
  fontSizeOverride?: string | [string, string, string, string]
  /** Responsive max-width cap, in `theme.responsiveStyle` array form. */
  maxWidthOverride?: [string, string, string, string]
}

/**
 * A navigational CTA that looks like a Citrus button but is a real anchor (`<a href>`) under the
 * hood — Citrus has no link-styled-as-button primitive, and elements that navigate to another
 * page should be links, not buttons. The visual variant travels as `buttonVariant`, stripped
 * before it reaches the DOM (mirrors `InlineLink`), so it never collides with Citrus `Link`'s own
 * `variant` prop.
 */
// Dark-mode CTA colours are sourced from the Citrus Secondary ramp and chosen to meet WCAG AA on the
// dark section backgrounds these CTAs sit on (e.g. ColdGrey_800 #333D47): rest `Secondary_200`
// (#A29EFF ≈4.67:1, AA text), hover `Secondary_100` (#CBC7FF ≈6.9:1), focus outline White (visible on
// dark). The earlier `#9C99FF` rest was ≈4.43:1 — just under AA — so it was lightened one ramp step.
const DARK_MODE_COLOR = {
  hoverOverlay: 'rgba(255, 255, 255, 0.16)',
}

type StyledLinkProps = LinkProps & {
  buttonVariant?: CWCLinkButtonVariant
  colorMode?: CWCLinkButtonColorMode
  fontSizeOverride?: string | [string, string, string, string]
  maxWidthOverride?: [string, string, string, string]
}

const StyledLink = withCitrusTheme<StyledLinkProps>(
  (props: StyledLinkProps) => {
    const { buttonVariant, colorMode, fontSizeOverride, maxWidthOverride, ...linkProps } = props

    return <Link {...linkProps} />
  },
  (theme, props: StyledLinkProps) => {
    const isSecondary = props.buttonVariant === 'secondary'
    const isDark = props.colorMode === 'dark'
    const secondaryColor = isDark
      ? theme.color(Color.Secondary_200)
      : theme.color(Color.Secondary_400)

    return [
      {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        minHeight: '48px',
        padding: '10px 24px',
        borderRadius: '8px',
        fontFamily: theme.tokens.FontFamilies.bold,
        textDecoration: 'none',
        // 16px constant. Citrus Link with variant=None renders bare children (no Link1 wrapper), so
        // its `size` prop is ignored and the text would otherwise inherit the theme's ramping body
        // default (16→18 at >=768). A base fontSize on the anchor overrides that inherited value at
        // every breakpoint — and there's no competing same-element Citrus media rule to lose to.
        fontSize: '16px',
        backgroundColor: isSecondary ? 'transparent' : theme.color(Color.Secondary_400),
        color: isSecondary ? secondaryColor : theme.color(Color.White),
        border: isSecondary ? `2px solid ${secondaryColor}` : 'none',
        // Dark mode's hover is a translucent white wash bleeding past the border, not a solid
        // fill — a transparent-at-rest pseudo-element that `&:hover::after` colours in.
        '&::after': isDark
          ? {
              content: '""',
              position: 'absolute',
              inset: '-2px',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              pointerEvents: 'none',
            }
          : undefined,
        ':hover': isDark
          ? {
              borderColor: theme.color(Color.Secondary_100),
              color: theme.color(Color.Secondary_100),
            }
          : {
              backgroundColor: isSecondary
                ? theme.color(Color.Secondary_50)
                : theme.color(Color.Secondary_600),
              color: isSecondary ? theme.color(Color.Secondary_600) : theme.color(Color.White),
            },
        '&:hover::after': isDark ? { backgroundColor: DARK_MODE_COLOR.hoverOverlay } : undefined,
        // Match the Citrus Button focus-visible ring so keyboard focus is consistent with the design
        // system. Secondary is checked first so it applies in BOTH colour modes — the dark-background
        // CWCWeeklyMenu CTA (secondary + dark) then focuses identically to the control OurSuppliersSection
        // CTA, which is a Citrus Button colorVariant=Secondary. Citrus Secondary: Secondary_50 fill + a
        // 3px Secondary_600 inset border, no outer outline. Citrus Primary: Secondary_600 fill + a 1px
        // white inset ring + a 2px Secondary_600 outline. There is no dark-mode primary usage today; the
        // isDark branch keeps a White outline (visible on the dark sections these sit on, per develop's
        // WCAG note — Secondary_600 is near-invisible there) as a safe fallback for any future dark primary.
        // eslint-disable-next-line no-nested-ternary
        ':focus-visible': isSecondary
          ? {
              backgroundColor: theme.color(Color.Secondary_50),
              color: theme.color(Color.Secondary_600),
              boxShadow: `inset 0 0 0 3px ${theme.color(Color.Secondary_600)}`,
              // The box-shadow ring above is the visible focus indicator in normal rendering, but
              // forced-colors (Windows High Contrast) suppresses box-shadow and author colours. A
              // transparent outline is invisible normally yet is promoted to a system colour in
              // forced-colors, so keyboard focus stays visible there (the rest border alone can't
              // signal focus). outlineOffset places it clear of the button edge.
              outline: '2px solid transparent',
              outlineOffset: '2px',
            }
          : isDark
          ? {
              outline: `2px solid ${theme.color(Color.White)}`,
              outlineOffset: '2px',
            }
          : {
              backgroundColor: theme.color(Color.Secondary_600),
              color: theme.color(Color.White),
              boxShadow: `inset 0 0 0 1px ${theme.color(Color.White)}`,
              outline: `2px solid ${theme.color(Color.Secondary_600)}`,
              outlineOffset: '0',
            },
      },
      theme.responsiveStyle(['100%', null, '345px'], ['width']),
      // Secondary is 16px/24px per ui-design — Citrus's FontSize scale index 4 pairs exactly this
      // fontSize/lineHeight, so this sources both from the token table rather than hardcoding
      // either as a raw value. Primary keeps the inherited Link size.
      isSecondary ? theme.responsiveFont(4) : undefined,
      // A plain string applies one size at every breakpoint (a base fontSize on the anchor wins with
      // no competing same-element Citrus media rule); an array varies it per breakpoint via responsiveStyle.
      // eslint-disable-next-line no-nested-ternary
      props.fontSizeOverride
        ? typeof props.fontSizeOverride === 'string'
          ? { fontSize: props.fontSizeOverride }
          : theme.responsiveStyle(props.fontSizeOverride, ['fontSize'])
        : undefined,
      props.maxWidthOverride
        ? theme.responsiveStyle(props.maxWidthOverride, ['maxWidth'])
        : undefined,
    ]
  },
)

export const CWCLinkButton = ({
  href,
  children,
  onClick,
  variant = 'primary',
  tabIndex,
  colorMode,
  fontSizeOverride,
  maxWidthOverride,
}: CWCLinkButtonProps) => {
  // A real <a href> is already keyboard-accessible: it's in the tab order (Tab) and activates on
  // Enter natively. Anchors do NOT activate on Space (Space scrolls the page), so — because this link
  // is styled as a button — add Space activation for button-consistent keyboard operability. Arrow
  // keys keep their default (page scroll); they are not activation keys for a link or a button.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      event.currentTarget.click()
    }
  }

  return (
    <StyledLink
      href={href}
      variant={LinkVariant.None}
      buttonVariant={variant}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={tabIndex}
      colorMode={colorMode}
      fontSizeOverride={fontSizeOverride}
      maxWidthOverride={maxWidthOverride}
    >
      {children}
    </StyledLink>
  )
}
